import { cn } from "@/lib/utils";
import { Clock, GitBranch, Play } from "lucide-react";
import { Button } from "@/components/ui/button";

export interface Checkpoint {
  id: string;
  name: string;
  timestamp: Date;
  prompt: string;
  isCurrent?: boolean;
}

interface SessionCheckpointProps {
  checkpoint: Checkpoint;
  onLoad: (id: string) => void;
  onFork: (id: string) => void;
}

export function SessionCheckpoint({ checkpoint, onLoad, onFork }: SessionCheckpointProps) {
  const formatTime = (date: Date) => {
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  return (
    <div
      className={cn(
        "group relative p-4 rounded-xl border transition-all duration-200 cursor-pointer",
        checkpoint.isCurrent
          ? "bg-gradient-to-br from-plum/20 to-electric/10 border-plum/50"
          : "bg-card border-border/50 hover:border-accent/30 hover:bg-secondary/50"
      )}
    >
      {/* Current indicator */}
      {checkpoint.isCurrent && (
        <div className="absolute -left-px top-1/2 -translate-y-1/2 w-1 h-8 rounded-r-full bg-gradient-to-b from-plum to-electric" />
      )}

      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h4 className="font-medium text-foreground truncate">{checkpoint.name}</h4>
            {checkpoint.isCurrent && (
              <span className="px-2 py-0.5 text-[10px] font-medium rounded-full bg-accent/20 text-accent">
                Current
              </span>
            )}
          </div>
          <p className="text-xs text-muted-foreground truncate mb-2">{checkpoint.prompt}</p>
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <Clock className="w-3 h-3" />
            <span>{formatTime(checkpoint.timestamp)}</span>
          </div>
        </div>

        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={(e) => {
              e.stopPropagation();
              onLoad(checkpoint.id);
            }}
          >
            <Play className="w-3 h-3" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={(e) => {
              e.stopPropagation();
              onFork(checkpoint.id);
            }}
          >
            <GitBranch className="w-3 h-3" />
          </Button>
        </div>
      </div>
    </div>
  );
}
